import React, { useState, useEffect } from 'react';
import { Box, Grid, Paper, Typography, CircularProgress, IconButton } from '@mui/material';
import { FaUsers, FaCalendarCheck, FaUserClock, FaPlus } from 'react-icons/fa';
import axios from 'axios';
import { API_BASE_URL } from '../../App.js';
import ClubhouseBookingModal from './components/ClubhouseBookingModal';

const StatCard = ({ title, value, icon, color }) => (
  <Paper elevation={3} sx={{ p: 3, height: '100%', bgcolor: `${color}.50` }}>
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <Box>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {title}
        </Typography>
        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
          {value}
        </Typography>
      </Box>
      <Box sx={{ color: `${color}.main`, fontSize: 40 }}>
        {icon}
      </Box>
    </Box>
  </Paper>
);

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const fetchStats = () => {
    axios.get(`${API_BASE_URL}/api/stats/dashboard`)
      .then(response => {
        setStats(response.data);
        setLoading(false);
      })
      .catch(err => {
        console.error('Error fetching dashboard stats:', err);
        setError('Fehler beim Laden der Statistiken');
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchStats();
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      
      {/* Statistics Cards */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6} lg={3}>
          <StatCard
            title="Aktive Mitglieder"
            value={stats?.activeMembers || 0}
            icon={<FaUsers />}
            color="primary"
          />
        </Grid>
        
        <Grid item xs={12} md={6} lg={3}>
          <StatCard
            title="Kommende Buchungen"
            value={stats?.upcomingBookings || 0}
            icon={<FaCalendarCheck />}
            color="success"
          />
        </Grid>

        <Grid item xs={12} md={6} lg={3}>
          <StatCard
            title="Unbezahlte Gaststunden"
            value={stats?.unpaidGuests || 0}
            icon={<FaUserClock />}
            color="warning"
          />
        </Grid>
      </Grid>

      {/* Clubhouse Widget - Separate Row */}
      <Grid container spacing={3} sx={{ mt: 0 }}>
        <Grid item xs={12} md={6} lg={4}>
            <Paper elevation={3} sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">Clubheim</Typography>
                    <IconButton size="small" color="primary" onClick={() => setShowModal(true)}>
                        <FaPlus />
                    </IconButton>
                </Box>
                
                {stats?.nextClubhouseBookings && stats.nextClubhouseBookings.length > 0 ? (
                    <Box>
                        {stats.nextClubhouseBookings.slice(0, 3).map((booking, index) => (
                            <Box key={booking._id} sx={{ 
                                display: 'flex', 
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                gap: 1,
                                py: 1, 
                                borderBottom: index < 2 ? '1px solid #eee' : 'none' 
                            }}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                            <Typography sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', lineHeight: 1.2 }} className="fs-7">
                                                {new Date(booking.date).toLocaleDateString('de-DE')}
                                            </Typography>
                                            {booking.startTime && (
                                                <Typography color="text.secondary" sx={{ fontSize: '0.85em', whiteSpace: 'nowrap' }} className="fs-7">
                                                    ab {booking.startTime} Uhr
                                                </Typography>
                                            )}
                                        </Box>
                                        <Typography noWrap className="fs-7" sx={{ alignSelf: booking.startTime ? 'flex-start' : 'center', pt: booking.startTime ? 0.2 : 0 }}>
                                            {booking.rentedBy && booking.rentedBy.firstName ? `${booking.rentedBy.firstName} ${booking.rentedBy.lastName}` : 'Unbekannt'}
                                        </Typography>
                                    </Box>
                                </Box>
                                
                                {booking.notes && (
                                    <Typography 
                                        color="text.secondary" 
                                        sx={{ 
                                            maxWidth: '35%', 
                                            textAlign: 'right', 
                                            whiteSpace: 'nowrap', 
                                            overflow: 'hidden', 
                                            textOverflow: 'ellipsis',
                                            display: { xs: 'none', sm: 'block' }
                                        }} 
                                        className="fs-7"
                                    >
                                        {booking.notes}
                                    </Typography>
                                )}
                            </Box>
                        ))}
                        {stats.nextClubhouseBookings.length > 3 && (
                             <Typography variant="caption" color="primary" sx={{ display: 'block', textAlign: 'center', mt: 1, cursor: 'pointer' }} onClick={() => window.location.href='/admin/clubhouse'}>
                                + {stats.nextClubhouseBookings.length - 3} weitere
                             </Typography>
                        )}
                    </Box>
                ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                        Keine bevorstehenden Reservierungen.
                    </Typography>
                )}
            </Paper>
        </Grid>
        
      </Grid>

      <ClubhouseBookingModal
          show={showModal}
          onHide={() => setShowModal(false)}
          booking={null}
          refresh={fetchStats}
      />
    </Box>
  );
};

export default Dashboard;